import { useState } from "react";
import useContent from "@lib/use_content";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import Session from "@lib/session";
import ContinueButton from "@components/ContinueButton";
import Error from "@hudson/ui/components/Error";
import Success from "@hudson/ui/components/Success";
import { FormProvider, useForm } from "react-hook-form";
import SavedCards from "./SavedCards";
import CreditCard from "@components/CreditCard";
import ConfirmationDialog from "@hudson/ui/components//Dialogs/Confirmation";
import userProfileAddCc from "@services/user_profile_add_cc";
import userProfileDeleteCc from "@services/user_profile_delete_cc";

export default function CreditCards({ cards }: { cards: be.TokenizedCreditCard[] }) {
  const { data: cData, loading } = useContent([
    "beConfirmDialogDefaultCancelButton",
    "beErrProfileCcAdd",
    "beProfileCcAddSubmitButton",
    "beProfileCcAddTitle",
    "beErrProfileCcRemove",
    "beProfileCcRemoveDialogMessage",
    "beProfileCcRemoveDialogSubmitButton",
    "beProfileCcRemoveSuccessMsg",
    "beProfileCcRemoveDialogTitle",
    "beProfileCcSavedCardsTitle",
  ]);
  const formMethods = useForm({});
  const [errorMessage, setErrorMessage] = useState<string>();
  const [success, setSuccess] = useState<boolean>();
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>();
  const [showConfirmation, setShowConfirmation] = useState<be.TokenizedCreditCard | null>(null);
  const [submitting, setSubmitting] = useState(false);

  interface NewCcData {
    ccaddress?: string;
    ccnumber: string;
    ccexpiration: string;
    ccname: string;
    ccpostalCode?: string;
  }

  async function deleteCard(ccId: string, ccRefId: string, ccLast4: string) {
    const userId = Session.getBooking().profiles.user;
    if (!userId) return;

    setErrorMessage("");
    setDeleteSuccess(false);
    setSuccess(false);

    const success = await userProfileDeleteCc(userId, String(ccId), ccRefId, undefined, ccLast4);

    if (!success) {
      setErrorMessage(cData?.content.beErrProfileCcRemove);
    } else {
      formMethods.reset();
      setDeleteSuccess(true);
    }
  }

  async function submitForm(data: NewCcData) {
    const userId = Session.getBooking().profiles.user;
    if (!userId) return;

    setErrorMessage("");
    setSubmitting(true);
    setDeleteSuccess(false);
    setSuccess(false);

    const success = await userProfileAddCc(
      userId,
      data.ccnumber,
      data.ccexpiration,
      data.ccname,
      data.ccpostalCode,
      data.ccaddress,
    );

    if (!success) {
      setErrorMessage(cData?.content.beErrProfileCcAdd);
    } else {
      formMethods.reset();
      setSuccess(true);
    }

    setSubmitting(false);
  }

  return (
    <>
      {loading ? (
        <ConcentricLoader className="opacity-60 max-w-2xl mx-auto  px-4 sm:py-10 sm:px-6 lg:px-8" />
      ) : (
        <FormProvider {...formMethods}>
          <ConfirmationDialog
            cancelLabel={cData?.content.beConfirmDialogDefaultCancelButton}
            confirmLabel={cData?.content.beProfileCcRemoveDialogSubmitButton}
            message={cData?.content.beProfileCcRemoveDialogMessage}
            onConfirm={async () => {
              await deleteCard(
                showConfirmation?.localId,
                showConfirmation?.refId,
                showConfirmation?.numberLast4,
              );
              setShowConfirmation(null);
            }}
            onHide={() => {
              setShowConfirmation(null);
            }}
            show={!!showConfirmation}
            title={cData?.content.beProfileCcRemoveDialogTitle}
            zIndexClass="z-50"
          />

          <form
            id="credit-cards"
            onSubmit={(e) => {
              e.preventDefault();
              formMethods.handleSubmit(submitForm)();
            }}
            noValidate
          >
            <div className="divide-y divide-gray-200">
              <div>
                <div className="grid grid-cols-1 gap-y-12 mt-8">
                  {(deleteSuccess || cards?.length > 0) && (
                    <div>
                      {cards.length > 0 && (
                        <div className="mb-6 space-y-1">
                          <h3 className="text-xl leading-6 font-bold text-gray-900">
                            {cData?.content.beProfileCcSavedCardsTitle}
                          </h3>
                        </div>
                      )}

                      {deleteSuccess && (
                        <Success
                          message={cData?.content.beProfileCcRemoveSuccessMsg}
                          heavyStyle
                          scrollTo
                        />
                      )}

                      <SavedCards
                        cards={cards}
                        fieldPrefix="cc_"
                        onDelete={(card) => setShowConfirmation(card)}
                      />
                    </div>
                  )}

                  <div className="flex flex-col space-y-6">
                    <div>
                      <h3 className="text-xl leading-6 font-bold text-gray-900">
                        {cData?.content.beProfileCcAddTitle}
                      </h3>
                    </div>

                    <CreditCard fieldPrefix="cc" hideCvv />

                    <div>
                      {errorMessage && (
                        <Error className="mb-6" message={errorMessage} heavyStyle scrollTo />
                      )}
                      {success && (
                        <Success
                          className="mb-6"
                          message={cData?.content.beProfileCcAddSuccessMsg}
                          heavyStyle
                          scrollTo
                        />
                      )}
                      <ContinueButton
                        alignLeft
                        className="justify-self-start mt-2"
                        disabled={submitting}
                        label={cData?.content.beProfileCcAddSubmitButton}
                        loading={submitting}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      )}
    </>
  );
}
